import React, { useEffect, useState } from "react";
import { Save, Building2, Clock, Download, KeyRound, Moon } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { InlineLoader } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIMEZONES = [
  "Europe/Belgrade", "Europe/London", "Europe/Berlin", "Europe/Paris", "Europe/Athens",
  "Europe/Istanbul", "UTC", "America/New_York", "America/Los_Angeles", "Asia/Dubai",
];

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings", {
        beach_name: settings.beach_name,
        timezone: settings.timezone,
        reset_time: "00:00",
        export_prefix: settings.export_prefix,
      });
      setSettings(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Fill both password fields"); return; }
    setPwSaving(true);
    try {
      await api.put("/account/password", pw);
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPwSaving(false);
    }
  };

  if (loading || !settings) return <InlineLoader />;

  return (
    <div className="space-y-6 bb-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Settings</h1>
        <p className="text-sm text-stone-500">Manage your beach configuration</p>
      </div>

      <Section icon={Building2} title="Beach">
        <Field label="Beach name">
          <Input value={settings.beach_name} onChange={(e) => setSettings({ ...settings, beach_name: e.target.value })} data-testid="settings-beach-name" />
        </Field>
        <Field label="Timezone">
          <Select value={settings.timezone} onValueChange={(v) => setSettings({ ...settings, timezone: v })}>
            <SelectTrigger data-testid="settings-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </Section>

      <Section icon={Moon} title="Daily reset">
        <div className="flex items-center justify-between rounded-xl bg-stone-50 px-4 py-3">
          <div>
            <p className="text-sm font-semibold">Automatic daily reset</p>
            <p className="text-xs text-stone-500">All baldachins reset to Free. Previous days are saved as read-only history.</p>
          </div>
          <span className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-sm font-bold text-stone-700 shadow-sm">
            <Clock className="h-4 w-4" /> Midnight (00:00)
          </span>
        </div>
      </Section>

      <Section icon={Download} title="Export">
        <Field label="Export file prefix">
          <Input value={settings.export_prefix} onChange={(e) => setSettings({ ...settings, export_prefix: e.target.value })} data-testid="settings-export-prefix" />
          <p className="mt-1 text-xs text-stone-400">Excel files are named <b>{settings.export_prefix || "beach_report"}_YYYY-MM-DD.xlsx</b></p>
        </Field>
      </Section>

      <div>
        <Button onClick={save} disabled={saving} data-testid="settings-save" className="gap-2 rounded-xl font-semibold">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Settings"}
        </Button>
      </div>

      <Section icon={KeyRound} title="Account">
        <Field label="Current password">
          <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="settings-current-password" />
        </Field>
        <Field label="New password">
          <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="settings-new-password" />
        </Field>
        <Button variant="outline" onClick={changePassword} disabled={pwSaving} data-testid="settings-change-password" className="gap-2 rounded-xl font-semibold">
          <KeyRound className="h-4 w-4" /> {pwSaving ? "Updating…" : "Change Password"}
        </Button>
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-primary" strokeWidth={2.2} />
        <h2 className="font-heading text-lg font-bold">{title}</h2>
      </div>
      <div className="grid max-w-xl gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold">{label}</Label>
      {children}
    </div>
  );
}
